import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import {
  formatApiPaymentDisplayLabel,
  isUrdLinkedPaymentMode,
  parsePaymentModeFromApi,
  PAYMENT_METHOD_LABELS,
} from "../utils/paymentLinesErpAdapter";
import { getErpBranchDetails } from "../erpClient";
import { getSessionBranchId } from "../utils/sessionBranch";
import { getTenantNameUpper } from "../utils/tenantName";
import { capitalizeWords } from "../utils/capitalizeWords";
import {
  computeBillTaxRoundOff,
  toMoney,
} from "../utils/autoRoundOff";
import { getInvoiceCashReturnAmount } from "../utils/invoiceCashReturn";

// Register fonts if needed
Font.register({
  family: "Times New Roman",
  src: "https://db.onlinewebfonts.com/t/32441506567156636049eb850b53f02a.ttf",
});

// Create styles
// A4 + letterhead clearances (for print on letter-paid paper; no background image)
const A4_WIDTH = 595.28;
const A4_HEIGHT = 841.89;
const PAGE_PAD_TOP = 15;
const PAGE_PAD_BOTTOM = 15;
// Align content with letter-paid header box (same left/right line)
const LETTERHEAD_SIDE_INSET = 19;
const LETTERHEAD_HEADER_HEIGHT = 122;
// Small bottom pad so the table/footer land on the Manglik brand strip
const LETTERHEAD_FOOTER_CLEARANCE = 26;
const LETTERHEAD_BG_SRC =
  "/assets/images/letterhead/manglik-gold-letter-paid.png";
const INVOICE_META_HEIGHT = 42;
const CUSTOMER_BLOCK_HEIGHT = 82;
const TABLE_HEADER_HEIGHT = 20;
// Must cover totals + payments + jurisdiction + signature block (image + labels)
const TOTALS_FOOTER_HEIGHT = 195;
// Leave unused blank-row space so the signature is not clipped by overflow:hidden
const LAST_PAGE_BLANK_SAFETY = 28;

const styles = StyleSheet.create({
  page: {
    paddingTop: PAGE_PAD_TOP,
    paddingBottom: LETTERHEAD_FOOTER_CLEARANCE,
    paddingHorizontal: LETTERHEAD_SIDE_INSET,
    fontFamily: "Times-Roman",
  },
  letterheadBackground: {
    position: "absolute",
    top: 0,
    left: 0,
    width: A4_WIDTH,
    height: A4_HEIGHT,
  },
  container: {
    // Removing the common border
  },
  // Clear space for pre-printed Manglik Gold letterhead header when printing
  headerWithLetterhead: {
    height: LETTERHEAD_HEADER_HEIGHT,
  },
  headerContent: {
    alignItems: "center",
  },
  logoAndTitleSection: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  logoContainer: {
    width: 40,
    height: 40,
    marginRight: 8,
  },
  titleContainer: {
    alignItems: "center",
  },
  companyTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#9c7d38",
  },
  companyTagline: {
    fontSize: 10,
    marginTop: 2,
  },
  addressText: {
    fontSize: 8,
    textAlign: "center",
    marginTop: 2,
  },
  gstLine: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 4,
    marginTop: 2,
  },
  gstText: {
    fontSize: 8,
    fontWeight: "bold",
  },
  invoiceHeader: {
    border: "1pt solid #000000",
    borderRadius: 8,
    marginBottom: 3,
    overflow: "hidden",
  },
  mainContainer: {
    flexDirection: "row",
  },
  leftSection: {
    width: "66.66%",
  },
  rightSection: {
    width: "33.34%",
    borderLeft: "1pt solid #000000",
  },
  topSection: {
    borderBottom: "1pt solid #000000",
    padding: "2 4",
    flexDirection: "row",
  },
  bottomSection: {
    padding: "2 4",
  },
  gstin: {
    fontSize: 9,
    flex: 1,
    fontWeight: "bold",
  },
  taxInvoice: {
    fontSize: 9,
    fontWeight: "bold",
    flex: 1,
    textAlign: "center",
  },
  original: {
    fontSize: 9,
    textAlign: "center",
    fontWeight: "bold",
  },
  stateText: {
    fontSize: 9,
    fontWeight: "bold",
    marginBottom: 1,
  },
  customerSection: {
    border: "1pt solid #000000",
    borderRadius: 8,
    marginBottom: 3,
    overflow: "hidden",
  },
  customerHeader: {
    borderBottom: "1pt solid #000000",
    padding: "1 2",
    alignItems: "center",
    justifyContent: "center",
  },
  customerHeaderText: {
    fontSize: 7,
    fontWeight: "bold",
    textAlign: "center",
  },
  customerContent: {
    flexDirection: "row",
  },
  customerLeft: {
    width: "66.66%",
    borderRight: "1pt solid #000000",
    padding: "2 8",
    overflow: "hidden",
  },
  customerRight: {
    width: "33.34%",
    padding: "2 8",
    overflow: "hidden",
  },
  detailRow: {
    flexDirection: "row",
    marginBottom: 1,
    alignItems: "flex-start",
    width: "100%",
  },
  detailLabel: {
    fontSize: 9,
    width: 58,
    fontWeight: "bold",
    flexShrink: 0,
  },
  detailLabelRight: {
    fontSize: 9,
    width: 62,
    fontWeight: "bold",
    flexShrink: 0,
  },
  detailColon: {
    fontSize: 9,
    fontWeight: "bold",
    flexShrink: 0,
    width: 10,
  },
  detailValueWrap: {
    flex: 1,
    paddingRight: 2,
  },
  detailValue: {
    fontSize: 9,
    fontWeight: "bold",
  },
  tableHeader: {
    flexDirection: "row",
    borderTop: "1pt solid #000000",
    borderLeft: "1pt solid #000000",
    borderRight: "1pt solid #000000",
    borderBottom: "1pt solid #000000",
    fontSize: 9,
    fontWeight: "bold",
    minHeight: 20,
  },
  tableRow: {
    flexDirection: "row",
    borderLeft: "1pt solid #000000",
    borderRight: "1pt solid #000000",
    borderBottom: "1pt solid #000000",
    minHeight: 26,
    fontSize: 9,
  },
  emptyRow: {
    flexDirection: "row",
    minHeight: 26,
    fontSize: 9,
  },
  tableCol1: {
    width: "4%",
    borderRight: "1pt solid #000000",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 2,
  },
  tableCol2: {
    width: "40%",
    borderRight: "1pt solid #000000",
    position: "relative",
    padding: 2,
  },
  tableCol3: {
    width: "8%",
    borderRight: "1pt solid #000000",
    textAlign: "center",
    padding: 2,
  },
  tableCol4: {
    width: "8%",
    borderRight: "1pt solid #000000",
    textAlign: "center",
    padding: 2,
  },
  tableCol5: {
    width: "10%",
    borderRight: "1pt solid #000000",
    textAlign: "center",
    padding: 2,
  },
  tableCol6: {
    width: "10%",
    borderRight: "1pt solid #000000",
    textAlign: "center",
    padding: 2,
  },
  tableCol7: {
    width: "10%",
    borderRight: "1pt solid #000000",
    textAlign: "center",
    padding: 2,
  },
  tableCol8: {
    width: "8%",
    borderRight: "1pt solid #000000",
    textAlign: "center",
    padding: 2,
  },
  tableCol9: {
    width: "10%",
    textAlign: "center",
    padding: 2,
  },
  totalRow: {
    flexDirection: "row",
    borderBottom: "1pt solid #000000",
    fontSize: 9,
    padding: 2,
  },
  noteCell: {
    width: "6%",
    padding: 2,
    fontWeight: "bold",
    textAlign: "left",
  },
  emptyCell: {
    width: "55%",
    padding: 2,
    paddingLeft: 2,
    marginLeft: 0,
  },
  totalAmountCell: {
    width: "40%", // Increased to match tax section width
    padding: 2,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  totalAmountLabel: {
    fontSize: 9,
    fontWeight: "bold",
  },
  totalAmountValue: {
    fontSize: 9,
    fontWeight: "bold",
    width: "80px", // Fixed width to match tax values
    textAlign: "right",
  },
  totalLabelCell: {
    width: "10%",
    padding: 4,
    textAlign: "right",
    fontWeight: "bold",
  },
  totalValueCell: {
    width: "10%",
    padding: 4,
    textAlign: "center",
    fontWeight: "bold",
  },
  amountInWords: {
    width: "60%",
    padding: "2 4",
    borderRight: "1pt solid #000000",
    fontSize: 9,
    fontStyle: "italic",
  },
  taxSummary: {
    width: "40%",
    padding: 0,
  },
  taxRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 0,
    borderBottom: "1pt solid #000000",
    padding: "2 0",
    marginLeft: -2,
    marginRight: -2,
    paddingLeft: 6,
    paddingRight: 4,
  },
  taxLabel: {
    fontSize: 9,
    fontWeight: "bold",
  },
  taxValue: {
    fontSize: 9,
    paddingRight: 2,
  },
  paymentSection: {
    flexDirection: "row",
    border: "1pt solid #000000",
    marginTop: 4, // Reduced from 8
  },
  paymentLeft: {
    width: "60%", // Match amountInWords width
    padding: "2 4", // Reduced padding
    borderRight: "1pt solid #000000",
  },
  paymentRight: {
    width: "40%", // Match taxSummary width
    padding: "2 4", // Reduced padding
  },
  paymentMethod: {
    flexDirection: "row",
    fontSize: 9,
    marginBottom: 2,
  },
  paymentMethodLabel: {
    // width: 60,
    fontWeight: "bold",
    fontSize: 9,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  paymentRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 0,
    borderBottom: "1pt solid #000000",
    padding: "2 0",
    position: "relative",
    marginLeft: -2,
    marginRight: -2,
  },
  paymentLabel: {
    fontSize: 9,
    fontWeight: "bold",
    paddingLeft: 6,
  },
  paymentValue: {
    fontSize: 9,
    paddingRight: 4,
    textAlign: "right",
  },
  footer: {
    paddingTop: 4,
    paddingBottom: 6,
    paddingHorizontal: 2,
    border: "1pt solid #000000",
    marginTop: 0,
  },
  footerTopRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 1,
    marginTop: 0,
  },
  footerTopRight: {
    alignItems: "flex-end",
    paddingRight: 18,
  },
  jurisdictionText: {
    fontSize: 7,
    fontWeight: "bold",
  },
  certificationText: {
    fontSize: 7,
    fontStyle: "italic",
    lineHeight: 1.1,
  },
  signatureSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    position: "relative",
    marginTop: 0,
    paddingBottom: 6,
    minHeight: 58,
  },
  signatureColumn: {
    width: "30%", // Each column takes equal width
    alignItems: "center",
    justifyContent: "flex-end",
  },
  signatureColumnLeft: {
    width: "30%",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    marginTop: 14,
  },
  signatureColumnRight: {
    width: "30%",
    alignItems: "flex-end",
    flexDirection: "column",
    justifyContent: "space-between",
    alignSelf: "stretch",
    marginTop: 0,
    paddingRight: 18,
  },
  signatureColumnRightTop: {
    alignItems: "flex-end",
    justifyContent: "flex-start",
  },
  logoContainer: {
    width: 55,
    height: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  signatureText: {
    fontSize: 9,
    fontWeight: "bold",
  },
  companySignature: {
    fontSize: 9,
    fontWeight: "bold",
    marginTop: 0,
    marginBottom: 1,
  },
  signatureImage: {
    width: 88,
    height: 30,
    objectFit: "contain",
  },
  rightTopSection: {
    borderBottom: "1pt solid #000000",
    padding: "2 4",
    flex: 1,
  },
  tableHeaderText: {
    textAlign: "center",
    fontWeight: "bold",
  },
  itemNameContainer: {
    flex: 1,
    paddingRight: 20,
    paddingTop: 2,
    paddingBottom: 2,
  },
  itemName: {
    fontSize: 9,
    fontWeight: "bold",
    maxWidth: "85%",
    lineHeight: 1.2,
  },
  chargeDetails: {
    fontSize: 8,
    color: "#444444",
    marginTop: 1,
    marginBottom: 1,
    maxWidth: "85%",
  },
  chargeAmountLine: {
    fontSize: 8,
    color: "#444444",
    marginLeft: 16, // This will be added to the base padding
  },
  chargeFirstLine: {
    display: "flex",
    flexDirection: "row",
  },
  chargeSecondLine: {
    paddingLeft: 0,
    marginLeft: 0,
  },
  chargeAmount: {
    minWidth: 80,
    textAlign: "left",
  },
  purityText: {
    position: "absolute",
    right: 4,
    top: 2,
    fontSize: 9,
    fontWeight: "bold",
  },
  gstFooter: {
    marginTop: 2,
  },
});

// Quotation A4 invoice — same layout as InvoicePDF with quote-specific labels
// embedLetterhead: only for download — stamps Manglik letter-pad under the quote
const QuoteInvoiceA4 = ({ invoiceData, embedLetterhead = false }) => {
  // Remove demo data and use the actual data passed from parent
  const data = invoiceData;
  const customer = data.customer || {};
  const invoiceItems = Array.isArray(data.items) ? data.items : [];
  const invoiceItemsWithSerial = invoiceItems.map((item, index) => ({
    ...item,
    __globalSerial: index + 1,
  }));

  const sessionBranchId = getSessionBranchId();
  const branchList = getErpBranchDetails();
  const branchDetails =
    branchList.find((b) => Number(b?.id) === Number(sessionBranchId)) ||
    branchList[0] ||
    {};
  const companyPan =
    branchDetails.pan_card || branchDetails.pan_no || branchDetails.pan || "-";
  const companyNameUpper = getTenantNameUpper();

  const gstPercent = parseFloat(data.gst_percent || 0) || 0;
  const baseSgst = parseFloat(data.sgst || 0) || 0;
  const baseCgst = parseFloat(data.cgst || 0) || 0;
  const baseIgst = parseFloat(data.igst || 0) || 0;
  const baseGstAmt =
    parseFloat(data.gst_amount ?? data.tax_amount ?? data.totalTax ?? 0) || 0;
  const customerState = String(
    data.customer?.state ||
    data.customer_state ||
    data.customerDetails?.state ||
    "",
  )
    .trim()
    .toLowerCase();
  const isGujarat =
    customerState === "" ||
    customerState.includes("gujarat") ||
    customerState === "gj";
  const noTaxAmounts =
    baseSgst + baseCgst + baseIgst <= 0 && baseGstAmt > 0 && gstPercent > 0;
  const sgstAmount = noTaxAmounts && isGujarat ? baseGstAmt / 2 : baseSgst;
  const cgstAmount = noTaxAmounts && isGujarat ? baseGstAmt / 2 : baseCgst;
  const igstAmount = noTaxAmounts && !isGujarat ? baseGstAmt : baseIgst;
  const addGstAmount = sgstAmount + cgstAmount + igstAmount || baseGstAmt;
  const totalBeforeTax = parseFloat(data.final_amount || data.subtotal || 0) || 0;
  const discountAmountForRound =
    data.discount && parseFloat(data.discount) > 0
      ? parseFloat(data.discount_amount || 0) || 0
      : 0;
  const displayAddGstAmount = toMoney(addGstAmount);
  const {
    roundOff: autoRoundOffAmount,
    amountAfterTax,
    roundOffLabel,
  } = computeBillTaxRoundOff({
    totalBeforeTax,
    discountAmount: discountAmountForRound,
    sgst: displayAddGstAmount,
  });
  const getPaymentLabel = (payment) => formatApiPaymentDisplayLabel(payment);
  const getPaymentAmount = (payment) => parseFloat(payment?.amount ?? 0) || 0;
  const isOldMetalPayment = (payment) =>
    isUrdLinkedPaymentMode(payment?.payment_mode) ||
    Number(payment?.urd_document_id) > 0;
  const paymentList = Array.isArray(data.payments) ? data.payments : [];
  const oldMetalPayments = paymentList.filter((p) => isOldMetalPayment(p));
  const customerPayments = paymentList.filter((p) => !isOldMetalPayment(p));
  const leftColumnPayments = [...oldMetalPayments, ...customerPayments];
  // Shrink payment lines when many are present so they don't overlap in the PDF
  const paymentCount = leftColumnPayments.length;
  const paymentFontSize =
    paymentCount <= 2
      ? 9
      : paymentCount === 3
        ? 7.5
        : paymentCount === 4
          ? 6.5
          : paymentCount === 5
            ? 5.5
            : 5;
  const paymentMetaFontSize = Math.max(4.5, paymentFontSize - 1);
  const paymentRowMinHeight = paymentFontSize + (paymentCount >= 4 ? 1 : 3);
  const paymentRowMarginTop = paymentCount <= 2 ? 2 : paymentCount <= 4 ? 1 : 0;
  const cashReturnAmount = getInvoiceCashReturnAmount(data);
  const getOldMetalPaymentLabel = (payment) => {
    const payMode = parsePaymentModeFromApi(payment?.payment_mode);
    return PAYMENT_METHOD_LABELS[payMode] || "Old Gold";
  };
  const resolveOldMetalPaymentNwt = (payment) => {
    const direct = parseFloat(payment?.nwt ?? 0);
    if (Number.isFinite(direct) && direct > 0) return direct;

    const urdId = Number(
      payment?.urd_document_id ??
      payment?.urdDocumentId ??
      payment?.old_purchase_id ??
      payment?.oldPurchaseId ??
      0,
    );
    if (!urdId) return 0;

    const oldPurchases = Array.isArray(data.old_purchase)
      ? data.old_purchase
      : [];
    const linkedDoc = oldPurchases.find((doc) => {
      const docId = Number(
        doc?.id ??
        doc?.old_metal_purchase_document_id ??
        doc?.urd_document_id ??
        0,
      );
      return docId === urdId;
    });
    if (!linkedDoc) return 0;

    const docTotalNwt = parseFloat(
      linkedDoc?.total_nwt ?? linkedDoc?.totalNwt ?? 0,
    );
    if (Number.isFinite(docTotalNwt) && docTotalNwt > 0) return docTotalNwt;

    const items = Array.isArray(linkedDoc?.items) ? linkedDoc.items : [];
    return items.reduce(
      (sum, item) => sum + (parseFloat(item?.nwt ?? 0) || 0),
      0,
    );
  };
  const getOldMetalNwtText = (payment) => {
    const nwt = resolveOldMetalPaymentNwt(payment);
    if (!Number.isFinite(nwt) || nwt <= 0) return "";
    const formatted = Number(nwt.toFixed(3)).toString();
    return ` (NWt: ${formatted})`;
  };
  const getDisplayItemName = (item = {}) => {
    const rawName =
      item?.name ??
      item?.item_category_name ??
      item?.product_group_name ??
      item?.design ??
      item?.tag_no ??
      "";
    const safeName =
      rawName === null || rawName === undefined ? "" : String(rawName);
    return safeName.includes("-")
      ? safeName.split("-").slice(1).join("-").trim()
      : safeName;
  };
  const estimateItemNameLines = (item = {}) => {
    const name = getDisplayItemName(item);
    if (!name) return 1;

    // Approximate chars that fit per line in the ITEM NAME column.
    const charsPerLine = 28;
    const lines = String(name)
      .split(/\r?\n/)
      .reduce(
        (sum, part) => sum + Math.max(1, Math.ceil(part.length / charsPerLine)),
        0,
      );
    return Math.max(1, lines);
  };
  const getItemRowMinHeight = (item = {}) => {
    const chargeCount = Array.isArray(item.charges) ? item.charges.length : 0;
    const nameLines = estimateItemNameLines(item);
    const hasIssue = Boolean(item.issueDescription);
    const hasHuid = Boolean(item.huid && String(item.huid).trim());

    // Keep row height close to content so separators render immediately after details.
    const computed =
      8 +
      nameLines * 11 +
      (hasIssue ? 10 : 0) +
      chargeCount * 11 +
      (hasHuid ? 11 : 0);

    return Math.max(26, computed);
  };

  const headerSpacerHeight = LETTERHEAD_HEADER_HEIGHT;
  // Reserve strip space for physical letter-paid print
  const bottomClearance = LETTERHEAD_FOOTER_CLEARANCE;
  const contentBandHeight =
    A4_HEIGHT - PAGE_PAD_TOP - bottomClearance - headerSpacerHeight;

  // Stretch item-table blank rows so the table end overlaps the Manglik strip.
  // Note + totals + signature footer is shown on EVERY page, so all pages use
  // the with-footer body height budget.
  const pageBodyWithFooter = Math.max(
    120,
    contentBandHeight -
    INVOICE_META_HEIGHT -
    CUSTOMER_BLOCK_HEIGHT -
    TABLE_HEADER_HEIGHT -
    TOTALS_FOOTER_HEIGHT,
  );

  // Hard cap: first page shows at most 7 items; overflow goes to next page(s).
  const FIRST_PAGE_MAX_ITEMS = 7;
  const CONTINUATION_PAGE_MAX_ITEMS = 7;

  const fillPageFromQueue = (queue, limit, maxItems = Infinity) => {
    const page = [];
    let height = 0;
    while (queue.length > 0 && page.length < maxItems) {
      const rowHeight = getItemRowMinHeight(queue[0]);
      if (page.length > 0 && height + rowHeight > limit) break;
      if (page.length === 0 && rowHeight > limit) {
        page.push(queue.shift());
        break;
      }
      page.push(queue.shift());
      height += rowHeight;
    }
    return page;
  };

  const splitItemsIntoPages = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) {
      return [[]];
    }

    // 7 or fewer items → single page when they fit with footer
    if (items.length <= FIRST_PAGE_MAX_ITEMS) {
      const totalHeight = items.reduce(
        (sum, item) => sum + getItemRowMinHeight(item),
        0,
      );
      if (totalHeight <= pageBodyWithFooter) {
        return [items];
      }
    }

    const queue = [...items];
    const pages = [
      fillPageFromQueue(queue, pageBodyWithFooter, FIRST_PAGE_MAX_ITEMS),
    ];

    while (queue.length > 0) {
      pages.push(
        fillPageFromQueue(
          queue,
          pageBodyWithFooter,
          CONTINUATION_PAGE_MAX_ITEMS,
        ),
      );
    }

    return pages;
  };
  const pageItemGroups = splitItemsIntoPages(invoiceItemsWithSerial);
  const totalPages = pageItemGroups.length;

  return (
    <Document>
      {pageItemGroups.map((pageItems, pageIndex) => {
        const isFirstPage = pageIndex === 0;
        const occupiedTableBodyHeight = pageItems.reduce(
          (sum, item) => sum + getItemRowMinHeight(item),
          0,
        );
        const tableBodyTarget = pageBodyWithFooter;
        const trailingBlankRowHeight = Math.max(
          0,
          tableBodyTarget - occupiedTableBodyHeight - LAST_PAGE_BLANK_SAFETY,
        );

        return (
          <Page
            key={`invoice-page-${pageIndex}`}
            size="A4"
            style={styles.page}
            wrap={false}
          >
            {embedLetterhead ? (
              <Image
                src={LETTERHEAD_BG_SRC}
                style={styles.letterheadBackground}
                fixed
              />
            ) : null}
            <View style={styles.container}>
              {/* Empty space for pre-printed letterhead header when printing */}
              <View style={styles.headerWithLetterhead}>
                {/* <View style={styles.headerContent}>
              <View style={styles.logoAndTitleSection}>
                <View style={styles.logoContainer}>
                  
                  <Image src="/assets/images/logos/logo_1.png" />
                </View>
                <View style={styles.titleContainer}>
                  <Text style={styles.companyTitle}>{companyNameUpper}</Text>
                  <Text style={styles.companyTagline}>Be Bold Try Gold</Text>
                </View>
              </View>
              
              <Text style={styles.addressText}>
                34/2-3, JAY SHREE SWAMINARAYAN NAGAR PART -2, NIKOL GAM ROAD{'\n'}
                NIKOL, AHMEDABAD - 382350
              </Text>
            </View>

            <View style={styles.gstLine}>
              <Text style={styles.gstText}>GST : 24AAHHD7497M1ZO</Text>
              <Text style={styles.gstText}>Mo.: 93777 35850</Text>
            </View> */}
              </View>

              {/* Full page-1 chrome on every page (replica for continuations) */}
              <>
                {/* Invoice Header */}
                <View style={styles.invoiceHeader}>
                    <View style={styles.mainContainer}>
                      <View style={styles.leftSection}>
                        <View style={styles.topSection}>
                          <Text style={styles.gstin}> </Text>
                        </View>
                        <View style={styles.bottomSection}>
                          <Text style={styles.stateText}>STATE : GUJARAT</Text>
                          <Text style={styles.stateText}>STATE CODE : 24</Text>
                        </View>
                      </View>
                      <View style={styles.rightSection}>
                        <View style={styles.rightTopSection}>
                          <Text style={styles.original}>ORIGINAL</Text>
                        </View>
                        <View style={styles.bottomSection}>
                          <Text style={styles.stateText}>
                            DATE : {data.date}
                          </Text>
                          <Text style={styles.stateText}>
                            NO : {data.invoiceNumber}
                          </Text>
                        </View>
                      </View>
                    </View>
                  </View>

                  {/* Customer Details */}
                  <View style={styles.customerSection}>
                    <View style={styles.customerHeader}>
                      <Text style={styles.customerHeaderText}>
                        Details of Receiver | Billed to:
                      </Text>
                    </View>

                    <View style={styles.customerContent}>
                      <View style={styles.customerLeft}>
                        <View style={styles.detailRow}>
                          <Text style={styles.detailLabel}>NAME</Text>
                          <Text style={styles.detailColon}>:</Text>
                          <View style={styles.detailValueWrap}>
                            <Text style={styles.detailValue}>
                              {capitalizeWords(customer.name) || "-"}
                            </Text>
                          </View>
                        </View>
                        <View style={styles.detailRow}>
                          <Text style={styles.detailLabel}>ADDRESS</Text>
                          <Text style={styles.detailColon}>:</Text>
                          <View style={styles.detailValueWrap}>
                            <Text style={styles.detailValue}>
                              {customer.address || "-"}
                            </Text>
                          </View>
                        </View>
                        <View style={styles.detailRow}>
                          <Text style={styles.detailLabel}>CITY</Text>
                          <Text style={styles.detailColon}>:</Text>
                          <View style={styles.detailValueWrap}>
                            <Text style={styles.detailValue}>
                              {customer.city || "-"}
                            </Text>
                          </View>
                        </View>
                        <View style={styles.detailRow}>
                          <Text style={styles.detailLabel}>STATE</Text>
                          <Text style={styles.detailColon}>:</Text>
                          <View style={styles.detailValueWrap}>
                            <Text style={styles.detailValue}>
                              {customer.state === "0"
                                ? "24-Gujarat"
                                : customer.state || ""}
                            </Text>
                          </View>
                        </View>
                      </View>

                      <View style={styles.customerRight}>
                        <View style={styles.detailRow}>
                          <Text style={styles.detailLabelRight}>MO</Text>
                          <Text style={styles.detailColon}>:</Text>
                          <View style={styles.detailValueWrap}>
                            <Text style={styles.detailValue}>
                              {customer.mobile || "-"}
                            </Text>
                          </View>
                        </View>
                        <View style={styles.detailRow}>
                          <Text style={styles.detailLabelRight}>ADHAR NO</Text>
                          <Text style={styles.detailColon}>:</Text>
                          <View style={styles.detailValueWrap}>
                            <Text style={styles.detailValue}>
                              {customer.aadhar_no || "-"}
                            </Text>
                          </View>
                        </View>
                      </View>
                    </View>
                  </View>
                </>

              {/* Item Table through Footer - Single Container */}
              <View
                style={{
                  border: "1pt solid #000000",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                {/* Table Header */}
                <View
                  style={[
                    styles.tableHeader,
                    { borderTop: "0", borderLeft: "0", borderRight: "0" },
                  ]}
                >
                  <Text style={styles.tableCol1}>No.</Text>
                  <View style={styles.tableCol2}>
                    <Text style={[styles.tableHeaderText, { marginRight: 20 }]}>
                      ITEM NAME
                    </Text>
                    <Text
                      style={{
                        position: "absolute",
                        right: 4,
                        top: 2,
                        fontSize: 9,
                        fontWeight: "bold",
                      }}
                    >
                      Ct.
                    </Text>
                  </View>
                  <Text style={styles.tableCol4}>HSN</Text>
                  <Text style={styles.tableCol5}>GR.WT</Text>
                  <Text style={styles.tableCol6}>NET WT.</Text>
                  <Text style={styles.tableCol7}>RATE</Text>
                  <Text style={styles.tableCol8}>OTHER</Text>
                  <Text style={styles.tableCol9}>AMOUNT</Text>
                </View>

                {/* Table Rows */}
                {pageItems.map((item, index) => (
                  <View
                    key={index}
                    style={[
                      styles.tableRow,
                      {
                        borderLeft: "0",
                        borderRight: "0",
                        borderBottom:
                          index < pageItems.length - 1
                            ? "1pt solid #000000"
                            : "0",
                        minHeight: getItemRowMinHeight(item),
                      },
                    ]}
                  >
                    <Text style={styles.tableCol1}>{item.__globalSerial}</Text>
                    <View style={styles.tableCol2}>
                      <View style={styles.itemNameContainer}>
                        <Text style={styles.itemName}>
                          {getDisplayItemName(item)}
                        </Text>
                        {item.issueDescription && (
                          <Text style={styles.chargeDetails}>
                            {item.issueDescription}
                          </Text>
                        )}
                        <View style={{ paddingLeft: 0 }}>
                          {item.charges &&
                            item.charges.map((charge, chargeIndex) => {
                              // Format the charge display based on rate_on value
                              const cleanChargeType = charge.charge_type
                                .replace(/(charge|charges)/gi, "")
                                .replace(/\s+s\b/g, "")
                                .trim();

                              // Check if this is HALLMARK, DIAMOND, or RHODIUM
                              const isSpecialCharge =
                                /^(HALLMARK|DIAMOND|RHODIUM)$/i.test(
                                  cleanChargeType,
                                );

                              return (
                                <View
                                  key={chargeIndex}
                                  style={styles.chargeDetails}
                                >
                                  <Text
                                    style={
                                      isSpecialCharge
                                        ? {
                                          fontSize: 9,
                                          fontWeight: "bold",
                                          color: "#000000",
                                        }
                                        : {}
                                    }
                                  >
                                    {cleanChargeType}
                                  </Text>
                                </View>
                              );
                            })}
                        </View>
                        {item.huid && item.huid.trim() && (
                          <View
                            style={[
                              styles.chargeDetails,
                              { flexDirection: "row", marginTop: 2 },
                            ]}
                          >
                            <Text
                              style={{
                                fontSize: 10,
                                fontWeight: "bold",
                                color: "#000000",
                              }}
                            >
                              HUID:{" "}
                            </Text>
                            <Text
                              style={{
                                fontSize: 10,
                                fontWeight: "normal",
                                color: "#000000",
                              }}
                            >
                              {item.huid}
                            </Text>
                          </View>
                        )}
                      </View>
                      <Text style={styles.purityText}>
                        {(() => {
                          const carat = parseFloat(item?.carat);
                          return Number.isFinite(carat) && carat > 0
                            ? `${parseInt(carat, 10)}c`
                            : "";
                        })()}
                      </Text>
                      {/* Pcs details in Ct. position */}
                      {item.charges &&
                        item.charges.map((charge, chargeIndex) => {
                          // Calculate base top: paddingTop (2) + item name (~11px) + issueDescription if exists (~15px)
                          const itemNameHeight = 11;
                          const issueDescriptionHeight = item.issueDescription
                            ? 15
                            : 0;
                          const baseTop =
                            2 + itemNameHeight + issueDescriptionHeight;
                          // Each charge line: marginTop (1) + fontSize with lineHeight (8-9px) + marginBottom (1) = ~11px
                          const lineHeight = 11;
                          const topPosition =
                            baseTop + chargeIndex * lineHeight;
                          return (
                            <View
                              key={chargeIndex}
                              style={{
                                position: "absolute",
                                right: 4,
                                top: topPosition,
                                fontSize: 8,
                                fontWeight: "bold",
                              }}
                            >
                              <Text>
                                {charge.ct && parseFloat(charge.ct) > 0
                                  ? `${parseInt(parseFloat(charge.ct) || 0)}`
                                  : charge.pcs && parseFloat(charge.pcs) > 0
                                    ? `${charge.pcs}`
                                    : ""}
                              </Text>
                            </View>
                          );
                        })}
                    </View>
                    <Text style={[styles.tableCol4, { paddingTop: 2 }]}>
                      {item.hsn}
                    </Text>
                    <View
                      style={[
                        styles.tableCol5,
                        { paddingTop: 2, position: "relative" },
                      ]}
                    >
                      <Text>{item.grossWeight || "0.000"}</Text>
                      {/* Weight details from charges - aligned with charge lines */}
                      {item.charges &&
                        item.charges.map((charge, chargeIndex) => {
                          // Calculate base top: paddingTop (2) + item name (~11px) + issueDescription if exists (~15px)
                          const itemNameHeight = 11;
                          const issueDescriptionHeight = item.issueDescription
                            ? 15
                            : 0;
                          const baseTop =
                            2 + itemNameHeight + issueDescriptionHeight;
                          // Each charge line: marginTop (1) + fontSize with lineHeight (8-9px) + marginBottom (1) = ~11px
                          const lineHeight = 11;
                          const topPosition =
                            baseTop + chargeIndex * lineHeight;
                          return (
                            <View
                              key={chargeIndex}
                              style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                top: topPosition,
                                fontSize: 8,
                                alignItems: "center",
                              }}
                            >
                              <Text
                                style={{
                                  fontSize: 8,
                                  lineHeight: 1.2,
                                  textAlign: "center",
                                  color: "#444444",
                                }}
                              >
                                {charge.wt && parseFloat(charge.wt) > 0
                                  ? `${parseFloat(charge.wt).toFixed(2)}`
                                  : ""}
                              </Text>
                            </View>
                          );
                        })}
                    </View>
                    <Text style={[styles.tableCol6, { paddingTop: 2 }]}>
                      {item.netWeight || "0.000"}
                    </Text>
                    <View
                      style={[
                        styles.tableCol7,
                        { paddingTop: 2, position: "relative" },
                      ]}
                    >
                      <Text>{`${parseFloat(item.rate || 0).toFixed(0)} + L.C.`}</Text>
                      {/* Rate details from charges - aligned with charge lines */}
                      {item.charges &&
                        item.charges.map((charge, chargeIndex) => {
                          // Calculate base top: paddingTop (2) + item name (~11px) + issueDescription if exists (~15px)
                          const itemNameHeight = 11;
                          const issueDescriptionHeight = item.issueDescription
                            ? 15
                            : 0;
                          const baseTop =
                            2 + itemNameHeight + issueDescriptionHeight;
                          // Each charge line: marginTop (1) + fontSize with lineHeight (8-9px) + marginBottom (1) = ~11px
                          const lineHeight = 11;
                          const topPosition =
                            baseTop + chargeIndex * lineHeight;
                          return (
                            <View
                              key={chargeIndex}
                              style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                top: topPosition,
                                fontSize: 8,
                                alignItems: "center",
                              }}
                            >
                              <Text
                                style={{
                                  fontSize: 8,
                                  lineHeight: 1.2,
                                  textAlign: "center",
                                  color: "#444444",
                                }}
                              >
                                {charge.rate && parseFloat(charge.rate) > 0
                                  ? charge.rate_on === "pcs"
                                    ? `${parseFloat(charge.rate).toFixed(0)}`
                                    : charge.rate_on === "ct"
                                      ? `${parseFloat(charge.rate).toFixed(0)}ct`
                                      : charge.rate_on === "wt"
                                        ? `${parseFloat(charge.rate).toFixed(0)}wt`
                                        : `${parseFloat(charge.rate).toFixed(0)}`
                                  : ""}
                              </Text>
                            </View>
                          );
                        })}
                    </View>
                    <View
                      style={[
                        styles.tableCol8,
                        { paddingTop: 2, position: "relative" },
                      ]}
                    >
                      <Text>{parseFloat(item.othamt || 0).toFixed(2)}</Text>
                      {/* Charge amounts - aligned with charge lines */}
                      {item.charges &&
                        item.charges.map((charge, chargeIndex) => {
                          // Calculate base top: paddingTop (2) + item name (~11px) + issueDescription if exists (~15px)
                          const itemNameHeight = 11;
                          const issueDescriptionHeight = item.issueDescription
                            ? 15
                            : 0;
                          const baseTop =
                            2 + itemNameHeight + issueDescriptionHeight;
                          // Each charge line: marginTop (1) + fontSize with lineHeight (8-9px) + marginBottom (1) = ~11px
                          const lineHeight = 11;
                          const topPosition =
                            baseTop + chargeIndex * lineHeight;
                          return (
                            <View
                              key={chargeIndex}
                              style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                top: topPosition,
                                fontSize: 8,
                                alignItems: "center",
                              }}
                            >
                              <Text
                                style={{
                                  fontSize: 8,
                                  lineHeight: 1.2,
                                  textAlign: "center",
                                  color: "#444444",
                                }}
                              >
                                {charge.amount && parseFloat(charge.amount) > 0
                                  ? `${parseFloat(charge.amount).toFixed(0)}`
                                  : ""}
                              </Text>
                            </View>
                          );
                        })}
                    </View>
                    <Text style={[styles.tableCol9, { paddingTop: 2 }]}>
                      {parseFloat(item.amount || 0).toFixed(2)}
                    </Text>
                  </View>
                ))}

                {trailingBlankRowHeight > 0 && (
                  <View
                    style={[
                      styles.emptyRow,
                      {
                        borderLeft: "0",
                        borderRight: "0",
                        borderBottom: "0",
                        minHeight: trailingBlankRowHeight,
                      },
                    ]}
                  >
                    <Text style={[styles.tableCol1, { borderBottom: 0 }]}>
                      {" "}
                    </Text>
                    <View style={[styles.tableCol2, { borderBottom: 0 }]}>
                      <Text> </Text>
                    </View>
                    <Text style={[styles.tableCol4, { borderBottom: 0 }]}>
                      {" "}
                    </Text>
                    <Text style={[styles.tableCol5, { borderBottom: 0 }]}>
                      {" "}
                    </Text>
                    <Text style={[styles.tableCol6, { borderBottom: 0 }]}>
                      {" "}
                    </Text>
                    <Text style={[styles.tableCol7, { borderBottom: 0 }]}>
                      {" "}
                    </Text>
                    <Text style={[styles.tableCol8, { borderBottom: 0 }]}>
                      {" "}
                    </Text>
                    <Text style={[styles.tableCol9, { borderBottom: 0 }]}>
                      {" "}
                    </Text>
                  </View>
                )}

                {/* Note + totals + signature — shown on every page */}
                <View style={{ borderTop: "1pt solid #000000" }}>
                  <View
                        style={[
                          styles.totalRow,
                          { borderLeft: 0, borderRight: 0 },
                        ]}
                      >
                        <Text style={styles.noteCell}>NOTE:</Text>
                        <Text style={styles.emptyCell}>{data.note || ""}</Text>
                        <View style={styles.totalAmountCell}>
                          <Text style={styles.totalAmountLabel}>
                            TOTAL AMOUNT
                          </Text>
                          <Text style={styles.totalAmountValue}>
                            {parseFloat(data.final_amount).toFixed(2)}
                          </Text>
                        </View>
                      </View>
                    </View>

                    {/* Amount in Words & Tax Summary Section */}
                    <View
                      style={{ flexDirection: "row", alignItems: "stretch" }}
                    >
                      <View
                        style={[
                          styles.amountInWords,
                          {
                            borderRight: "1pt solid #000000",
                            flexDirection: "column",
                          },
                        ]}
                      >
                        <Text>{data.amountInWords}</Text>
                        <View
                          style={{
                            borderBottom: "1pt solid #000000",
                            marginTop: 2,
                            marginLeft: -4,
                            marginRight: -4,
                          }}
                        />
                        <View
                          style={{
                            flexDirection: "row",
                            alignItems: "stretch",
                            flexGrow: 1,
                            marginLeft: -4,
                            marginRight: -4,
                          }}
                        >
                          {/* Left — URD (Old Gold/Silver) first, then Cash/Bank/etc. */}
                          <View
                            style={{
                              width: "60%",
                              borderRight: "1pt solid #000000",
                              alignSelf: "stretch",
                            }}
                          >
                            <View style={{ padding: "2 4" }}>
                              {leftColumnPayments.map((payment, index) => {
                                const isOldMetal = isOldMetalPayment(payment);
                                return (
                                  <View
                                    key={`pay-${index}`}
                                    wrap={false}
                                    style={{
                                      flexDirection: "row",
                                      flexWrap: "nowrap",
                                      alignItems: "flex-start",
                                      marginTop:
                                        index === 0
                                          ? paymentRowMarginTop
                                          : paymentRowMarginTop,
                                      marginBottom: paymentCount >= 5 ? 0 : 1,
                                      minHeight: paymentRowMinHeight,
                                    }}
                                  >
                                    <Text
                                      style={{
                                        fontWeight: "bold",
                                        fontSize: paymentFontSize,
                                      }}
                                    >
                                      {isOldMetal
                                        ? getOldMetalPaymentLabel(payment)
                                        : getPaymentLabel(payment)}{" "}
                                      :
                                    </Text>
                                    <Text style={{ fontSize: paymentFontSize }}>
                                      {" "}
                                      {getPaymentAmount(payment).toFixed(2)}
                                    </Text>
                                    {isOldMetal &&
                                      getOldMetalNwtText(payment) && (
                                        <Text
                                          style={{
                                            fontSize: paymentMetaFontSize,
                                            color: "#444444",
                                            marginLeft: 2,
                                          }}
                                        >
                                          {getOldMetalNwtText(payment)}
                                        </Text>
                                      )}
                                    {!isOldMetal && payment.remarks && (
                                      <Text
                                        style={{
                                          fontSize: paymentMetaFontSize,
                                          color: "#666666",
                                          marginLeft: 2,
                                        }}
                                      >
                                        ({payment.remarks})
                                      </Text>
                                    )}
                                  </View>
                                );
                              })}
                            </View>
                          </View>

                          {/* Right — cash return only when a JAAVAK entry exists */}
                          <View
                            style={{
                              width: "40%",
                              alignSelf: "stretch",
                            }}
                          >
                            <View style={{ padding: "2 4" }}>
                              {cashReturnAmount > 0 && (
                                <View
                                  wrap={false}
                                  style={{
                                    flexDirection: "row",
                                    alignItems: "flex-start",
                                    marginTop: 2,
                                    minHeight: paymentRowMinHeight,
                                  }}
                                >
                                  <Text
                                    style={{
                                      fontWeight: "bold",
                                      fontSize: paymentFontSize,
                                    }}
                                  >
                                    Cash Return :
                                  </Text>
                                  <Text
                                    style={{ fontSize: paymentFontSize }}
                                  >
                                    {" "}
                                    {cashReturnAmount.toFixed(2)}
                                  </Text>
                                </View>
                              )}
                            </View>
                          </View>
                        </View>
                        {/* {data.followup_date && (
                  <View style={[styles.paymentMethod, { marginTop: 4 }]}>
                    <Text style={[styles.paymentMethodLabel, { width: 60 }]}>
                      FOLLOWUP
                    </Text>
                    <Text style={{ fontSize: 9 }}>{data.followup_date}</Text>
                  </View>
                )} */}
                      </View>
                      <View
                        style={[
                          styles.taxSummary,
                          { position: "relative", overflow: "hidden" },
                        ]}
                      >
                        {data.discount && parseFloat(data.discount) > 0 && (
                          <View style={styles.taxRow}>
                            <Text style={styles.taxLabel}>
                              DISCOUNT ({parseFloat(data.discount).toFixed(2)}%)
                            </Text>
                            <Text style={styles.taxValue}>
                              {parseFloat(data.discount_amount || 0).toFixed(2)}
                            </Text>
                          </View>
                        )}
                        <View style={styles.taxRow}>
                          <Text style={styles.taxLabel}>ADD GST (3%)</Text>
                          <Text style={styles.taxValue}>
                            {displayAddGstAmount.toFixed(2)}
                          </Text>
                        </View>
                        <View style={styles.taxRow}>
                          <Text style={styles.taxLabel}>{roundOffLabel}</Text>
                          <Text style={styles.taxValue}>
                            {autoRoundOffAmount.toFixed(2)}
                          </Text>
                        </View>
                        <View style={[styles.taxRow, { borderBottom: 0 }]}>
                          <Text style={styles.taxLabel}>AMOUNT AFTER TAX</Text>
                          <Text style={styles.taxValue}>
                            {amountAfterTax.toFixed(2)}
                          </Text>
                        </View>
                      </View>
                    </View>

                    {/* Payment Details Section */}
                    <View
                      style={{
                        flexDirection: "row",
                        borderTop: "1pt solid #000000",
                        borderBottom: "1pt solid #000000",
                      }}
                    >
                      <View
                        style={[
                          styles.amountInWords,
                          { borderRight: "1pt solid #000000" },
                        ]}
                      >
                        <View style={styles.gstFooter}>
                          <Text
                            style={{
                              fontSize: 9,
                              fontWeight: "bold",
                              marginBottom: 2,
                            }}
                          >
                            PAN : {companyPan}
                          </Text>
                          <Text style={{ fontSize: 9, fontWeight: "bold" }}>
                            BIS HALLMARKING LIC. NO : HM/C-7290572420
                          </Text>
                        </View>
                      </View>
                      <View
                        style={[
                          styles.taxSummary,
                          { position: "relative", overflow: "hidden" },
                        ]}
                      >
                        <View style={styles.paymentRow}>
                          <Text style={styles.paymentLabel}>
                            ADVANCE PAYMENT
                          </Text>
                          <Text style={styles.paymentValue}>
                            {data.advancePayment || "0.00"}
                          </Text>
                        </View>
                        <View style={styles.paymentRow}>
                          <Text style={styles.paymentLabel}>TOTAL PAID</Text>
                          <Text style={styles.paymentValue}>
                            {data.paidAmount || "0.00"}
                          </Text>
                        </View>
                        <View style={[styles.paymentRow, { borderBottom: 0 }]}>
                          <Text style={styles.paymentLabel}>
                            FINAL OUTSTANDING
                          </Text>
                          <Text style={styles.paymentValue}>
                            {data.remainingAmount || "0.00"}
                          </Text>
                        </View>
                      </View>
                    </View>

                    {/* Footer Section */}
                    <View
                      style={[styles.footer, { border: "0", marginTop: 0 }]}
                    >
                      <View style={styles.footerTopRow}>
                        <Text style={styles.jurisdictionText}>
                          SUBJECT TO AHMEDABAD JURISDICTION.
                        </Text>
                        <View style={styles.footerTopRight}>
                          <Text style={styles.certificationText}>
                            Certified that the particulars given above are true
                            and correct.
                          </Text>
                        </View>
                      </View>

                      <View style={styles.signatureSection}>
                        <View style={styles.signatureColumnLeft}>
                          <Text style={{ height: 22 }}></Text>
                          <Text style={styles.signatureText}>
                            CUSTOMER SIGNATURE
                          </Text>
                        </View>
                        <View style={styles.signatureColumn}>
                          <View style={styles.logoContainer}>
                            <Image
                              src="https://i.ibb.co/gZs5jqnh/Bureau-of-Indian-Standards-Logo-svg.png"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                            />
                          </View>
                        </View>
                        <View style={styles.signatureColumnRight}>
                          <View style={styles.signatureColumnRightTop}>
                            <Text style={styles.companySignature}>
                              For, {companyNameUpper}
                            </Text>
                            <Image
                              src="/assets/images/logos/signature.png"
                              style={styles.signatureImage}
                            />
                          </View>
                          <Text style={styles.signatureText}>
                            AUTHORISED SIGNATURE
                          </Text>
                        </View>
                      </View>
                    </View>
              </View>
            </View>
          </Page>
        );
      })}
    </Document>
  );
};

export default QuoteInvoiceA4;
